import { Injectable } from '@angular/core';
import { ProductData } from '@types';
import { parseNumber } from '@utils/text/number-utils';
import get from 'lodash/get';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FundFilterService {
  private subject: Subject<[string, any[]]> = new Subject();
  private products: any;
  private filteredProducts: any;

  constructor() {}

  init(products: any[]): void {
    this.products = products;
    this.sendNext('initialList', this.products);
  }

  getData(): Observable<[string, any[]]> {
    return this.subject.asObservable();
  }

  private sendNext(type: string, productData: any[]) {
    this.subject.next([type, productData]);
  }

  filter(filterRequest: FilterRequest) {
    this.filterBySelection(filterRequest);
    this.sendNext('filteredList', this.filteredProducts);
  }

  private filterBySelection(filterRequest: FilterRequest) {
    this.filteredProducts = this.products.filter((product) => {
      return this.isSelected(filterRequest, product);
    });
  }

  private isSelected(filterRequest: FilterRequest, data: ProductData): boolean {
    let productSelected = true;
    for (const [filter, values] of filterRequest.selectedFilters.entries()) {
      let filterResult = false;
      for (const path of filter.paths) {
        const pathResult = filter.predicate(
          values,
          get(data, path, null),
          filterRequest
        );
        if (pathResult) {
          filterResult = true;
          break;
        }
      }
      if (!filterResult) {
        productSelected = false;
        break;
      }
    }
    return productSelected;
  }
}

export interface FilterRequest {
  selectedFilters: Map<BaseFilter, string[]>;
}

const defaultMultiValueFilterPredicate: (
  values: string[],
  data: any,
  filterRequest: FilterRequest
) => boolean = (values, data) => {
  return values.indexOf(String(data).trim()) > -1;
};
export class BaseFilter {
  constructor(
    public key: string,
    public predicate: (
      values: string[],
      data: any,
      filterRequest: FilterRequest
    ) => boolean,
    public paths: string[],
    public excludeValuesFromFilter: string[] = []
  ) {}
}

export class FundFilter extends BaseFilter {
  public static readonly PRIMARY_SHARE_CLASS = new FundFilter(
    'PRIMARY_SHARE_CLASS',
    defaultMultiValueFilterPredicate,
    ['shareClass.primaryShareClass']
  );
  public static readonly ASSET_CLASS = new FundFilter(
    'ASSET_CLASS',
    defaultMultiValueFilterPredicate,
    ['assetClass']
  );
  public static readonly SFDR = new FundFilter(
    'SFDR',
    defaultMultiValueFilterPredicate,
    ['sfdrCategory']
  );
  public static readonly INVESTMENT_MANAGER = new FundFilter(
    'INVESTMENT_MANAGER',
    defaultMultiValueFilterPredicate,
    ['investmentManager']
  );
  public static readonly SHARE_CLASS = new FundFilter(
    'SHARE_CLASS',
    defaultMultiValueFilterPredicate,
    ['shareClass.shareClassFeeStructure'],
    ['SINGLCLASS', 'NA', 'N/A']
  );
  public static readonly SHARE_CLASS_NAME = new FundFilter(
    'SHARE_CLASS_NAME',
    defaultMultiValueFilterPredicate,
    ['shareClass.shareClassName'],
    ['SINGLCLASS', 'NA', 'N/A']
  );
  public static readonly CLASS_STRATEGY = new FundFilter(
    'CLASS_STRATEGY',
    defaultMultiValueFilterPredicate,
    ['investmentCategory']
  );
  public static readonly INVESTMENT_VEHICLE = new FundFilter(
    'INVESTMENT_VEHICLE',
    defaultMultiValueFilterPredicate,
    ['investmentVehicle']
  );
  public static readonly CLASS_GEOGRAPHY = new FundFilter(
    'CLASS_GEOGRAPHY',
    defaultMultiValueFilterPredicate,
    ['region']
  );
  public static readonly INVESTMENT_GROUP = new FundFilter(
    'INVESTMENT_GROUP',
    defaultMultiValueFilterPredicate,
    ['investmentGroup']
  );
  public static readonly SEARCH_KEYWORD = new FundFilter(
    'SEARCH_KEYWORD',
    (values, data) => {
      return (
        values.filter((value) =>
          data ? data.toUpperCase().indexOf(value.toUpperCase()) > -1 : false
        ).length > 0
      );
    },
    [
      'fundName',
      'investmentGroup',
      'shareClass.shareClassFeeStructure',
      'shareClass.shareClassCurrency',
      'shareClass.identifiers.fundId',
      'shareClass.identifiers.shareClassCode',
      'shareClass.identifiers.ticker',
      'shareClass.identifiers.isin',
      'shareClass.identifiers.sedol',
      'shareClass.identifiers.bloomberg',
      'shareClass.identifiers.taid',
      'shareClass.identifiers.cusip',
      'shareClass.identifiers.reuterSystem',
      'shareClass.identifiers.countryCode',
      'shareClass.identifiers.languageCode',
      'shareClass.identifiers.mexId',
      'shareClass.identifiers.apir',
      'shareClass.identifiers.arsn',
      'shareClass.identifiers.anbimaCode',
      'shareClass.identifiers.vln',
      'shareClass.identifiers.intradayNav',
      'shareClass.identifiers.rfi',
      'shareClass.identifiers.adm',
      'shareClass.identifiers.dsc',
      'shareClass.identifiers.iress',
      'shareClass.identifiers.iressinavCode',
      'shareClass.identifiers.fundbmTicker',
      'shareClass.identifiers.wkn',
      'shareClass.identifiers.nasdaqxnms',
      'shareClass.identifiers.iopv',
      'shareClass.identifiers.frontend',
      'shareClass.identifiers.lowload',
      'shareClass.identifiers.dsc',
      'shareClass.identifiers.adm',
    ]
  );
  public static readonly FUND_CATEGORY_ASSET_CLASS = new FundFilter(
    'FUND_CATEGORY_ASSET_CLASS',
    defaultMultiValueFilterPredicate,
    ['fundCategory']
  );
  public static readonly CURRENCY = new FundFilter(
    'CURRENCY',
    defaultMultiValueFilterPredicate,
    ['shareClass.shareClassCurrency']
  );
  public static readonly ETF_TYPE = new FundFilter(
    'ETF_TYPE',
    defaultMultiValueFilterPredicate,
    ['etfType']
  );
  public static readonly FUND_CATEGORY = new FundFilter(
    'FUND_CATEGORY',
    defaultMultiValueFilterPredicate,
    ['fundCategory']
  );
  public static readonly WEB_STRATEGY = new FundFilter(
    'WEB_STRATEGY',
    defaultMultiValueFilterPredicate,
    ['strategy']
  );
  public static readonly MORNINGSTAR_RATING = new FundFilter(
    'MORNINGSTAR_RATING',
    (values, data) => {
      return (
        values.filter((value) => parseNumber(data) >= parseNumber(value))
          .length > 0
      );
    },
    ['shareClass.ratings.morningStarRatingOverall']
  );
  public static readonly FUND_FAMILY = new FundFilter(
    'FUND_FAMILY',
    defaultMultiValueFilterPredicate,
    ['fundFamily']
  );
  public static readonly METHODOLOGY = new FundFilter(
    'METHODOLOGY',
    defaultMultiValueFilterPredicate,
    ['methodology']
  );

  constructor(
    public key: string,
    public predicate: (
      values: string[],
      data: any,
      filterRequest: FilterRequest
    ) => boolean,
    public paths: string[],
    public excludeValuesFromFilter: string[] = []
  ) {
    super(key, predicate, paths, excludeValuesFromFilter);
  }
}

export class BaseLiteratureFilter extends BaseFilter {
  public static readonly INVESTMENT_MANAGER = new BaseLiteratureFilter(
    'INVESTMENT_MANAGER',
    defaultMultiValueFilterPredicate,
    ['product.investmentManager']
  );
  public static readonly INVESTMENT_VEHICLE = new BaseLiteratureFilter(
    'INVESTMENT_VEHICLE',
    defaultMultiValueFilterPredicate,
    ['product.investmentVehicle']
  );
  public static readonly ASSET_CLASS = new BaseLiteratureFilter(
    'ASSET_CLASS',
    defaultMultiValueFilterPredicate,
    ['product.assetClass']
  );
  public static readonly SFDR = new BaseLiteratureFilter(
    'SFDR',
    defaultMultiValueFilterPredicate,
    ['product.sfdrCategory']
  );
  public static readonly PRODUCT_TYPE = new BaseLiteratureFilter(
    'PRODUCT_TYPE',
    defaultMultiValueFilterPredicate,
    ['product.productType']
  );
  public static readonly ASSET_CATEGORY = new BaseLiteratureFilter(
    'ASSET_CATEGORY',
    defaultMultiValueFilterPredicate,
    ['literatureData.assetCategory']
  );
  public static readonly DATE = new BaseLiteratureFilter(
    'DATE',
    defaultMultiValueFilterPredicate,
    ['literatureData.frkReferenceYear']
  );

  public static readonly INVESTMENT_GROUP = new BaseLiteratureFilter(
    'INVESTMENT_GROUP',
    defaultMultiValueFilterPredicate,
    ['product.investmentGroup']
  );

  constructor(
    public key: string,
    public predicate: (
      values: string[],
      data: any,
      filterRequest: FilterRequest
    ) => boolean,
    public paths: string[],
    public excludeValuesFromFilter: string[] = []
  ) {
    super(key, predicate, paths, excludeValuesFromFilter);
  }

  public static isDataSelected(values: string[], data: any): boolean {
    let found = false;
    for (const element of data) {
      for (const value of values) {
        found =
          element?.trim().toUpperCase().indexOf(value.trim().toUpperCase()) >
          -1;
        if (found) {
          break;
        }
      }
      if (found) {
        break;
      }
    }
    return found;
  }
}

export class CustomLiteratureFilter extends BaseLiteratureFilter {
  public static readonly SHARE_CLASS = new CustomLiteratureFilter(
    'SHARE_CLASS',
    (values, data) => CustomLiteratureFilter.isDataSelected(values, data),
    ['shareClass']
  );

  public static readonly SEARCH_KEYWORD = new CustomLiteratureFilter(
    'SEARCH_KEYWORD',
    (values, data) => {
      return (
        values.filter((value) => {
          let found = false;
          if (Array.isArray(data)) {
            return CustomLiteratureFilter.isDataSelected(values, data);
          } else {
            found = data?.toUpperCase().indexOf(value?.toUpperCase()) > -1;
          }
          return found;
        }).length > 0
      );
    },
    [
      'product.fundId',
      'product.fundName',
      'product.assetClass',
      'product.sfdrcategory',
      'product.productType',
      'identifier.bloomberg',
      'identifier.taid',
      'identifier.sedol',
      'identifier.isin',
      'identifier.cusip',
      'identifier.reuterSystem',
      'identifier.wkn',
      'identifier.vln',
      'identifier.mexid',
      'identifier.rfi',
      'identifier.apir',
      'identifier.arsn',
      'identifier.adm',
      'identifier.dsc',
      'identifier.ticker',
      'identifier.iress',
      'identifier.iressinavcode',
      'identifier.anbimacode',
      'identifier.intradaynav',
      'identifier.fundbmticker',
    ]
  );
}

export class ListingLiteratureFilter extends BaseLiteratureFilter {
  public static readonly DOCUMENT_TYPE = new ListingLiteratureFilter(
    'DOCUMENT_TYPE',
    defaultMultiValueFilterPredicate,
    ['literatureData.dctermsType']
  );
  public static readonly CURRENCY = new ListingLiteratureFilter(
    'CURRENCY',
    (values, data) => BaseLiteratureFilter.isDataSelected(values, data),
    ['literatureData.identifiers.shareClassCurrency']
  );
  public static readonly DISTRIBUTION = new ListingLiteratureFilter(
    'DISTRIBUTION',
    (values, data) => BaseLiteratureFilter.isDataSelected(values, data),
    ['literatureData.identifiers.distributionIndicator']
  );
  public static readonly SHARE_CLASS = new ListingLiteratureFilter(
    'SHARE_CLASS',
    (values, data) => BaseLiteratureFilter.isDataSelected(values, data),
    ['literatureData.identifiers.shareClassFeeStructure']
  );
  public static readonly TOPICS = new ListingLiteratureFilter(
    'TOPICS',
    (values, data) => BaseLiteratureFilter.isDataSelected(values, data),
    ['literatureData.topic']
  );

  public static readonly SUB_TOPICS = new ListingLiteratureFilter(
    'SUB_TOPICS',
    defaultMultiValueFilterPredicate,
    ['literatureData.subTopic']
  );
  public static readonly CLIENT_USE = new ListingLiteratureFilter(
    'CLIENT_USE',
    defaultMultiValueFilterPredicate,
    ['literatureData.clientUse']
  );
  public static readonly LANGUAGE = new ListingLiteratureFilter(
    'LANGUAGE',
    (values, data) => BaseLiteratureFilter.isDataSelected(values, data),
    ['literatureData.languageFilterValues']
  );

  public static readonly SEARCH_KEYWORD = new ListingLiteratureFilter(
    'SEARCH_KEYWORD',
    (values, data) => {
      return (
        values.filter((value) => {
          let found = false;
          if (Array.isArray(data)) {
            return BaseLiteratureFilter.isDataSelected(values, data);
          } else {
            found = data?.toUpperCase().indexOf(value?.toUpperCase()) > -1;
          }
          return found;
        }).length > 0
      );
    },
    [
      'product.fundId',
      'product.fundName',
      'literatureData.fundName',
      'literatureData.docId',
      'product.assetClass',
      'product.sfdrcategory',
      'product.productType',
      'literatureData.keywords',
      'literatureData.dctermsType',
      'literatureData.identifiers.bloomberg',
      'literatureData.identifiers.taid',
      'literatureData.identifiers.sedol',
      'literatureData.identifiers.isin',
      'literatureData.identifiers.cusip',
      'literatureData.identifiers.reuterSystem',
      'literatureData.identifiers.wkn',
      'literatureData.identifiers.vln',
      'literatureData.identifiers.mexid',
      'literatureData.identifiers.rfi',
      'literatureData.identifiers.apir',
      'literatureData.identifiers.arsn',
      'literatureData.identifiers.adm',
      'literatureData.identifiers.dsc',
      'literatureData.identifiers.ticker',
      'literatureData.identifiers.iress',
      'literatureData.identifiers.iressinavcode',
      'literatureData.identifiers.anbimacode',
      'literatureData.identifiers.intradaynav',
      'literatureData.identifiers.fundbmticker',
      'literatureData.topic',
      'literatureData.subTopic',
      'literatureData.assetCategory',
    ]
  );
}
